import type { PrescriptionStaffApiOutContract } from '../api/generated/pwrdbyQuickMDAdminsApi.schemas'

type LegacyPrescription = {
  Brand: string
  CreatedAt: string
  Directions: string
  DispenseAmount: number
  DispenseUnits: string
  Doctor: { FirstName: string; LastName: string; Designation: string }
  Dosage: string
  DrugName: string
  Duration: string
  Frequency: string
  PrescriptionId: string
  Note: string
  Pharmacy: PrescriptionStaffApiOutContract['Pharmacy']
  PharmacyPartner: PrescriptionStaffApiOutContract['PharmacyPartner'] | null
  Quantity: string
  Refills: number
  Type: 'order' | 'treat'
  Status: string
  ShippingAddress: {
    StreetAddress1?: string | null
    StreetAddress2?: string | null
    City?: string | null
    StateCode?: string | null
    Zip?: string | null
  } | null
  Shipment: { Carrier: string; ShippedDate: string; TrackingNumber: string; TrackingUrl: string } | null
  UpdatedAt: string
  IsRefillOrder: boolean
  RefillsRemaining: number
  EncounterUrl: undefined
  TotalFillsRemaining: number | null
  LastOrderDate: string | null
  LastOrderStatus: string | null
}

export type { LegacyPrescription }

export function mapPrescriptionToLegacy(rx: PrescriptionStaffApiOutContract): LegacyPrescription {
  const lastOrder = rx.LastOrder
  const topLevelStatus = 'Status' in rx && typeof rx.Status === 'string' ? rx.Status : undefined

  // For an order we normally show the order's own fulfillment status
  // (LastOrder.Status: confirmed → senttopharmacy → shipped). But cancelling the
  // prescription sets the top-level Status to a cancelled state while leaving
  // LastOrder.Status on its last order value (e.g. still 'confirmed'), so a terminal
  // cancellation of the prescription must take precedence — otherwise the chart keeps
  // showing the stale order status and the cancel action never closes. These three
  // match ui_kit_qmd OrderDetails' isCanceled set exactly.
  const CANCELLED_PRESCRIPTION_STATUSES = ['cancel', 'cancelled', 'cancel_success']
  const isPrescriptionCancelled =
    topLevelStatus !== undefined && CANCELLED_PRESCRIPTION_STATUSES.includes(topLevelStatus.toLowerCase())
  const effectiveStatus = isPrescriptionCancelled ? topLevelStatus : (lastOrder?.Status ?? topLevelStatus)

  return {
    Brand: rx.Brand ?? '',
    CreatedAt: rx.CreatedAt ?? '',
    Directions: rx.Directions ?? '',
    DispenseAmount: rx.DispenseAmount ?? 0,
    DispenseUnits: rx.DispenseUnits ?? '',
    Doctor: {
      FirstName: rx.Doctor?.FirstName ?? '',
      LastName: rx.Doctor?.LastName ?? '',
      Designation: rx.Doctor?.Designation ?? '',
    },
    Dosage: rx.Dosage ?? '',
    DrugName: rx.DrugName ?? '',
    Duration: rx.Duration != null ? String(rx.Duration) : '',
    Frequency: rx.Frequency ?? '',
    PrescriptionId: rx.PrescriptionId ?? '',
    Note: rx.Note ?? '',
    Pharmacy: rx.Pharmacy,
    PharmacyPartner: rx.PharmacyPartner ?? null,
    Quantity: rx.Quantity != null ? String(rx.Quantity) : '',
    Refills: rx.Refills ?? 0,
    Type: rx.Type === 'order' ? 'order' : 'treat',

    // Fields restructured into LastOrder
    Status: (effectiveStatus ?? '').toUpperCase(),
    ShippingAddress: lastOrder?.ShippingAddress ?? null,
    Shipment:
      lastOrder?.Shipment ?
        {
          Carrier: lastOrder.Shipment.Carrier ?? 'usps',
          ShippedDate: lastOrder.Shipment.ShippedDate ?? '',
          TrackingNumber: lastOrder.Shipment.TrackingNumber ?? '',
          TrackingUrl: lastOrder.Shipment.TrackingUrl ?? '',
        }
      : null,
    UpdatedAt: lastOrder?.Date ?? rx.CreatedAt ?? '',
    IsRefillOrder: false,
    RefillsRemaining: rx.TotalFillsRemaining ?? 0,
    EncounterUrl: undefined,

    // New fields for the updated PrescriptionCard/PrescriptionPopUp
    TotalFillsRemaining: rx.TotalFillsRemaining ?? null,
    LastOrderDate: lastOrder?.Date ?? null,
    LastOrderStatus: lastOrder?.Status ? lastOrder.Status.toUpperCase() : null,
  }
}
